import React, { useState } from 'react';
import { Card, Table, Row, Col, Progress } from 'antd';
import { useOwnerRisk } from '@/hooks/useOwnerIntelligence';
import { useCurrency } from '@/contexts/CurrencyContext';
import { SignalBadge, OiPageFrame, OiDetailDrawer, OiRiskRowPanel } from '@/components/owner-intelligence/OwnerIntelligenceComponents';
import { OiRiskHeatmap } from '@/components/owner-intelligence/charts/OiCharts';

const RiskCenterPage: React.FC = () => {
  const { data, isLoading, error } = useOwnerRisk();
  const { formatCurrency } = useCurrency();
  const [drawerRow, setDrawerRow] = useState<Record<string, unknown> | null>(null);

  const risks = (data?.risks ?? []) as Array<{
    id: string;
    title: string;
    risk_score: number;
    impact: string;
    count: number;
    likelihood?: number;
  }>;

  const heatCells = risks.map((r, i) => ({
    x: Math.min(5, Math.max(1, Math.ceil((r.likelihood ?? (i % 5) + 1) / 20))),
    y: Math.min(5, Math.max(1, Math.ceil(r.risk_score / 20))),
    count: r.count || 1,
  }));

  return (
    <OiPageFrame
      subtitle="Executive risk register and improvement opportunities."
      loading={isLoading && !data}
      error={Boolean(error)}
      errorMessage="Risk center unavailable"
    >
      <Card title="Exception grid (illustrative)" bordered={false} style={{ marginBottom: 16 }}>
        <OiRiskHeatmap cells={heatCells} />
        <div style={{ fontSize: 11, color: 'var(--oi-text-muted)', marginTop: 8 }}>
          Grid position is illustrative — use the risk register below for details. Legend: 0–40 low · 41–70 watch · 71–100 critical
        </div>
      </Card>

      <Row gutter={16}>
        <Col xs={24} lg={12}>
          <Card title="Risk register" bordered={false}>
            <Table
              size="small"
              rowKey="id"
              dataSource={risks}
              onRow={(row) => ({
                onClick: () => setDrawerRow(row as Record<string, unknown>),
                style: { cursor: 'pointer' },
              })}
              columns={[
                { title: 'Risk', dataIndex: 'title' },
                { title: 'Score', dataIndex: 'risk_score' },
                {
                  title: 'Impact',
                  dataIndex: 'impact',
                  render: (v: string) => (
                    <SignalBadge tier={v === 'critical' ? 'critical' : 'watch'} />
                  ),
                },
                { title: 'Count', dataIndex: 'count' },
              ]}
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="Opportunities" bordered={false}>
            <Table
              size="small"
              rowKey="id"
              dataSource={data?.opportunities ?? []}
              columns={[
                { title: 'Opportunity', dataIndex: 'title' },
                { title: 'Potential', dataIndex: 'potential_value', render: (v: number) => formatCurrency(v) },
                {
                  title: 'Effort vs value',
                  render: (_, row: { effort?: string; potential_value?: number }) => (
                    <Progress
                      percent={row.effort === 'high' ? 30 : row.effort === 'medium' ? 55 : 80}
                      size="small"
                      format={() => formatCurrency(Number(row.potential_value ?? 0))}
                    />
                  ),
                },
              ]}
            />
          </Card>
        </Col>
      </Row>

      <OiDetailDrawer open={Boolean(drawerRow)} onClose={() => setDrawerRow(null)} title="Risk detail">
        {drawerRow ? <OiRiskRowPanel row={drawerRow} /> : null}
      </OiDetailDrawer>
    </OiPageFrame>
  );
};

export default RiskCenterPage;
